/// <amd-module name="Wit/DeliveryPlans/PlansTable/PlansTable" />
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { css, IVssComponentContext, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import {
    ColumnMore,
    ColumnSorting,
    IMeasurementStyle,
    ITableColumn,
    ITableRow,
    SimpleTableCell,
    sortItems,
    SortOrder,
    Table,
    TableCell
} from "VSSUI/Table";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { ago, AgoFormat } from "VSS/Platform/Date";
import { Filter } from "VSSUI/Utilities/Filter";
import { VssPersona } from "VSSUI/VssPersona";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { PlanUserPermissions } from "Wit/Clients/Work/Contracts/Work";
import { TableStatus } from "Wit/Common/WitSettingsViews/Components/TableStatus";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { hasPermission } from "../Common/Utils/PermissionUtils";
import { DeletePlanDialog, DeletePlanDialogProps } from "./DeletePlanDialog";
import { IPlansTableService, PlanFilterConstants, PlansTableRowData } from "./IPlansTableService";

export interface PlansTableProps {
    /**
     * Action when new plan button is activated
     */
    onActivateNewPlan: () => void;
}

const zeroDataAssetId = "ms.vss-work-web/delivery-plans-content/Content/plans-landing.svg";
const planPermissionNamespaceId = "BED337F8-E5F3-4FB9-80DA-81E17D06E7A8";

export class PlansTable extends VssComponent<PlansTableProps> {
    private _filter: Filter;
    private _sortingBehavior: ColumnSorting<PlansTableRowData>;

    private _favoritesService: IFavoritesService;
    private _identityService: IVssIdentityService;
    private _plansTableService: IPlansTableService;

    constructor(props: PlansTableProps, context: IVssComponentContext) {
        super(props, context);

        this._favoritesService = context.pageContext.getService<IFavoritesService>("IFavoritesService");
        this._identityService = context.pageContext.getService<IVssIdentityService>("IVssIdentityService");
        this._plansTableService = context.pageContext.getService<IPlansTableService>("IPlansTableService");

        this._filter = this._plansTableService.filter;
        this._sortingBehavior = new ColumnSorting<PlansTableRowData>(
            (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
                const sort = (columnIndex: number, sortOrder: SortOrder, rows: PlansTableRowData[]): PlansTableRowData[] => {
                    return sortItems<PlansTableRowData>(columnIndex, proposedSortOrder, this._sortingFunctions, this._columns, rows);
                };

                this._plansTableService.sortRows(columnIndex, proposedSortOrder, sort);
            }
        );

        const pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        pageHistoryService.registerFilter(this._filter, "plan", [
            {
                dataType: "string",
                filterKey: PlanFilterConstants.TextFilterKey,
                historyBehavior: HistoryBehavior.replace
            }
        ]);
    }

    public render() {
        const errorMessage = this._plansTableService.dataProviderErrorMessage;
        const rows = this._plansTableService.rows;
        return (
            <div className="flex-grow flex-column">
                <Observer rows={rows}>
                    {(props: { rows: PlansTableRowData[] }) => {
                        let innerContent: JSX.Element;
                        const hasPlans = props.rows.length > 0;
                        if (hasPlans) {
                            innerContent = (
                                <Table
                                    ariaLabel={Resources.PlansTableAriaLabel}
                                    behaviors={[this._sortingBehavior]}
                                    className="delivery-plans-table"
                                    columns={this._columns}
                                    itemProvider={rows}
                                    onActivate={this._onActivateRow}
                                />
                            );
                        } else {
                            const image = getAssetLocation(this.context.pageContext, zeroDataAssetId);
                            innerContent = (
                                <div className="flex-column flex-grow flex-center margin-bottom-16">
                                    <div className="flex-row flex-grow justify-center">
                                        <ZeroData
                                            imagePath={image}
                                            imageAltText={""}
                                            primaryText={Resources.ZeroPlanDataPrimaryText}
                                            secondaryText={Resources.ZeroPlanDataSecondaryText}
                                            actionText={Resources.ZeroPlanDataActionText}
                                            actionType={ZeroDataActionType.ctaButton}
                                            onActionClick={this.props.onActivateNewPlan}
                                        />
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <>
                                <TableStatus className="margin-bottom-16" dataProviderErrorMessage={errorMessage} />
                                <Card className="flex-grow bolt-table-card" contentProps={{ contentPadding: false }}>
                                    <div className="flex-column flex-grow">
                                        <div className="flex-row">
                                            <Header title={Resources.DeliveryPlansHeader} titleSize={TitleSize.Medium} className="flex-grow" />
                                            <div className="settings-branches-filter margin-vertical-16 margin-horizontal-16">
                                                <InlineKeywordFilterBarItem
                                                    filter={this._filter}
                                                    filterItemKey={PlanFilterConstants.TextFilterKey}
                                                    placeholder={Resources.FilterPlansPlaceholder}
                                                />
                                            </div>
                                        </div>
                                        {innerContent}
                                    </div>
                                </Card>
                            </>
                        );
                    }}
                </Observer>
            </div>
        );
    }

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PlansTableRowData>,
        tableItem: PlansTableRowData
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                contentClassName="rhythm-horizontal-8"
                key={tableItem.id + "-col-" + columnIndex}
                tableColumn={tableColumn}
            >
                <Icon iconName="PlanView" />
                <span>{tableItem.name}</span>
            </SimpleTableCell>
        );
    };

    private _renderCreatedByColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PlansTableRowData>,
        tableItem: PlansTableRowData
    ): JSX.Element => {
        const identity = tableItem.createdByIdentity;
        // this identityRef doesn't have an image URL, but we can get one from _identityService
        const imageUrl = this._identityService.getIdentityImageUrl(identity);
        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <VssPersona className="icon-margin" displayName={identity.displayName} imageUrl={imageUrl} size="extra-small-plus" />
                {identity.displayName}
            </SimpleTableCell>
        );
    };

    private _renderDescriptionColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PlansTableRowData>,
        tableItem: PlansTableRowData
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                {tableItem.description}
            </SimpleTableCell>
        );
    };

    private _renderLastConfiguredColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PlansTableRowData>,
        tableItem: PlansTableRowData
    ): JSX.Element => {
        const modifiedDate = tableItem.modifiedDate || tableItem.createdDate;
        const formattedDate = ago(modifiedDate, AgoFormat.Extended);
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                contentClassName="rhythm-horizontal-8"
                key={tableItem.id + "-col-" + columnIndex}
                tableColumn={tableColumn}
            >
                <Icon iconName="DateTime2" />
                <span>{formattedDate}</span>
            </SimpleTableCell>
        );
    };

    private _renderLastAccessedColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PlansTableRowData>,
        tableItem: PlansTableRowData
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                contentClassName="rhythm-horizontal-8"
                key={tableItem.id + "-col-" + columnIndex}
                tableColumn={tableColumn}
            >
                {tableItem.lastAccessed &&
                    <>
                        <Icon iconName="DateTime2" />
                        <span>{ago(tableItem.lastAccessed, AgoFormat.Extended)}</span>
                    </>
                }
            </SimpleTableCell>
        );
    };

    private _renderFavoriteCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PlansTableRowData>,
        tableItem: PlansTableRowData
    ): JSX.Element => {
        let cellContents = <span></span>;

        if (this._favoritesService.canUseFavorites()) {
            const removeFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                event.preventDefault();
                event.stopPropagation();
                this._plansTableService.unfavoritePlan(tableItem.id);
            };

            const addFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                event.preventDefault();
                event.stopPropagation();
                this._plansTableService.favoritePlan(tableItem.id);
            };

            cellContents = (
                <Observer isFavorite={tableItem.isFavoriteObservable}>
                    {(props: { isFavorite: boolean }) => {
                        let button: JSX.Element;
                        let alwaysVisible = false;
                        if (props.isFavorite) {
                            alwaysVisible = true;
                            button = (
                                <Button
                                    ariaLabel={Resources.UnfavoriteButtonAriaLabel}
                                    className="favorite-icon-color"
                                    excludeTabStop
                                    iconProps={{ iconName: "FavoriteStarFill" }}
                                    onClick={removeFavorite}
                                    subtle
                                />
                            );
                        } else {
                            button = (
                                <Button
                                    ariaLabel={Resources.FavoriteButtonAriaLabel}
                                    excludeTabStop
                                    iconProps={{ iconName: "FavoriteStar" }}
                                    onClick={addFavorite}
                                    subtle
                                />
                            );
                        }

                        return (
                            <div className={css("flex-column", "flex-center", !alwaysVisible && "bolt-table-cell-content-reveal")}>
                                <div className="flex-row">{button}</div>
                            </div>
                        );
                    }}
                </Observer>
            );
        }

        return (
            <TableCell
                className="bolt-table-cell-side-action"
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={tableItem.id + "col-" + columnIndex}
            >
                {cellContents}
            </TableCell>
        );
    };

    private _onActivateRow = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<PlansTableRowData>) => {
        if (!event.defaultPrevented && tableRow.data.linkProps.href) {
            onClickFPS(this.context.pageContext, tableRow.data.linkProps.href, true, event);
        }
    };

    private _getMenuItems = (rowData: PlansTableRowData): IMenuProps => {
        const onDelete = (onDismiss: () => void) => {
            this._plansTableService.deletePlan(rowData.id);
            onDismiss();
        };

        const menuItems = [
            {
                iconProps: { iconName: "Shield" },
                id: "plan-security-command",
                important: false,
                onActivate: () => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout(onDismiss => {
                        const props = {
                            dependencies: ["ms.vss-admin-web.security-view-common-content"],
                            wrappedType: "ms.vss-admin-web.security-panel-component",
                            onCancel: onDismiss,
                            securityViewTitle: format(Resources.SecurityPanelTitle, rowData.name),
                            permissionSetId: planPermissionNamespaceId,
                            permissionSetToken: rowData.id
                        };

                        return React.createElement(WrappedComponent, props);
                    });
                },
                text: Resources.SecurityLabel
            } as IMenuItem,
            {
                disabled: !hasPermission(rowData.userPermissions, PlanUserPermissions.Delete),
                iconProps: { iconName: "Delete" },
                id: "delete-plan-command",
                important: false,
                onActivate: () => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout(onDismiss => {
                        const props: DeletePlanDialogProps = {
                            onDelete: () => onDelete(onDismiss),
                            onDismiss: onDismiss
                        };

                        return React.createElement(DeletePlanDialog, props);
                    });
                },
                text: Resources.DeleteButtonText
            } as IMenuItem
        ];

        return {
            id: "plan-menu",
            items: menuItems
        };
    };

    private _columns = [
        {
            id: "name",
            name: Resources.NameColumnLabel,
            renderCell: this._renderNameColumn,
            sortProps: {
                ariaLabelAscending: Resources.SortTextAscendingAriaLabel,
                ariaLabelDescending: Resources.SortTextDescendingAriaLabel
            },
            width: new ObservableValue(-30)
        },
        {
            id: "created-by",
            name: Resources.CreatedByColumnLabel,
            renderCell: this._renderCreatedByColumn,
            sortProps: {
                ariaLabelAscending: Resources.SortTextAscendingAriaLabel,
                ariaLabelDescending: Resources.SortTextDescendingAriaLabel
            },
            width: new ObservableValue(220)
        },
        {
            id: "description",
            name: Resources.DescriptionColumnLabel,
            renderCell: this._renderDescriptionColumn,
            sortProps: {
                ariaLabelAscending: Resources.SortTextAscendingAriaLabel,
                ariaLabelDescending: Resources.SortTextDescendingAriaLabel
            },
            width: new ObservableValue(-40)
        },
        {
            id: "last-configured-time",
            name: Resources.LastConfiguredColumnLabel,
            renderCell: this._renderLastConfiguredColumn,
            sortProps: {
                ariaLabelAscending: Resources.SortDateAscendingAriaLabel,
                ariaLabelDescending: Resources.SortDateDescendingAriaLabel
            },
            width: new ObservableValue(-25)
        },
        {
            id: "last-accessed-time",
            name: Resources.LastAccessedColumnLabel,
            renderCell: this._renderLastAccessedColumn,
            sortProps: {
                ariaLabelAscending: Resources.SortDateAscendingAriaLabel,
                ariaLabelDescending: Resources.SortDateDescendingAriaLabel
            },
            width: new ObservableValue(-25)
        },
        {
            ariaLabel: Resources.FavoriteCellAriaLabel,
            headerClassName: "flex-row justify-center",
            iconProps: { iconName: "FavoriteStar" },
            id: "favorite",
            renderCell: this._renderFavoriteCell,
            sortProps: {
                ariaLabelAscending: Resources.SortFavoritesAscendingAriaLabel,
                ariaLabelDescending: Resources.SortFavoritesDescendingAriaLabel
            },
            width: new ObservableValue(5),
            widthStyle: IMeasurementStyle.REM
        },
        new ColumnMore(this._getMenuItems)
    ];

    private _sortingFunctions = [
        // Sort on Name column
        (a: PlansTableRowData, b: PlansTableRowData): number => {
            return a.name.localeCompare(b.name, navigator.language);
        },
        // Sort on Created By column
        (a: PlansTableRowData, b: PlansTableRowData): number => {
            return a.createdByIdentity.displayName.localeCompare(b.createdByIdentity.displayName, navigator.language);
        },
        // Sort on Description column
        (a: PlansTableRowData, b: PlansTableRowData): number => {
            const descriptionA = a.description || "";
            const descriptionB = b.description || "";
            return descriptionA.localeCompare(descriptionB, navigator.language);
        },
        // Sort on Last Configured column
        (a: PlansTableRowData, b: PlansTableRowData): number => {
            const lastConfigured1 = a.modifiedDate || a.createdDate;
            const lastConfigured2 = b.modifiedDate || b.createdDate;
            return lastConfigured1 > lastConfigured2 ? 1 : -1;
        },
        // Sort on Last Accessed column
        (a: PlansTableRowData, b: PlansTableRowData): number => {
            const lastConfigured1 = a.lastAccessed ?? '';
            const lastConfigured2 = b.lastAccessed ?? '';
            return lastConfigured1 > lastConfigured2 ? 1 : -1;
        },
        // Sort on favorite column
        (a: PlansTableRowData, b: PlansTableRowData): number => {
            const aVal = a.isFavoriteObservable.value;
            const bVal = b.isFavoriteObservable.value;
            if (aVal && !bVal) {
                return 1;
            }

            if (bVal && !aVal) {
                return -1;
            }

            return 0;
        }
    ];
}
